import { Fragment } from 'react';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, BellAlertIcon } from '@heroicons/react/24/outline';
import { useNotifications } from '../contexts/NotificationContext';
import { TYPE_ICONS, DEFAULT_ICON } from '../constants/notificationTypes';

function timeAgo(dateString) {
  if (!dateString) return '';
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'à l\'instant';
  if (diffMin < 60) return `il y a ${diffMin} min`;
  if (diffHour < 24) return `il y a ${diffHour}h`;
  if (diffDay < 7) return `il y a ${diffDay}j`;
  if (diffDay < 30) return `il y a ${Math.floor(diffDay / 7)} sem.`;
  return `il y a ${Math.floor(diffDay / 30)} mois`;
}

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();

  const handleClick = (notification, close) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.data?.link) {
      navigate(notification.data.link);
    }
    close();
  };

  return (
    <Menu as="div" className="relative">
      {({ close }) => (
        <>
          <MenuButton className="relative rounded-lg p-1.5 text-skin-secondary transition-colors hover:bg-hover focus:outline-none">
            <span className="sr-only">Notifications</span>
            <BellIcon className="h-6 w-6" aria-hidden="true" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-surface-secondary">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </MenuButton>

          <MenuItems
            transition
            className="absolute right-0 z-50 mt-2 w-80 origin-top-right rounded-lg border border-skin bg-surface-elevated shadow-dropdown transition focus:outline-none data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in sm:w-96"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-skin px-4 py-3">
              <h3 className="text-sm font-semibold text-skin-primary">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    markAllAsRead();
                  }}
                  className="text-xs font-medium text-primary-500 hover:text-primary-600"
                >
                  Tout marquer comme lu
                </button>
              )}
            </div>

            {/* Notification list */}
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 px-4">
                  <BellAlertIcon className="h-10 w-10 text-skin-tertiary" />
                  <p className="mt-2 text-sm text-skin-tertiary">Aucune notification</p>
                </div>
              ) : (
                <div className="py-1">
                  {notifications.map((notification) => {
                    const Icon = TYPE_ICONS[notification.type] || DEFAULT_ICON;
                    return (
                      <MenuItem key={notification.id} as={Fragment}>
                        <button
                          type="button"
                          onClick={() => handleClick(notification, close)}
                          className={`group flex w-full items-start gap-3 px-4 py-3 text-left transition-colors data-focus:bg-hover ${
                            !notification.is_read ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''
                          }`}
                        >
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40">
                            <Icon className="h-4 w-4 text-primary-600" aria-hidden="true" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className={`text-sm ${!notification.is_read ? 'font-semibold text-skin-primary' : 'font-normal text-skin-secondary'}`}>
                              {notification.title}
                            </p>
                            {notification.message && (
                              <p className="mt-0.5 line-clamp-2 text-xs text-skin-tertiary">
                                {notification.message}
                              </p>
                            )}
                            <p className="mt-1 text-xs text-skin-tertiary">
                              {timeAgo(notification.created_at)}
                            </p>
                          </div>
                          {!notification.is_read && (
                            <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary-500" />
                          )}
                        </button>
                      </MenuItem>
                    );
                  })}
                </div>
              )}
            </div>
          </MenuItems>
        </>
      )}
    </Menu>
  );
}
